import { APP_LINKS } from '@/constants/seo';

export type AppStoreTarget = 'ios' | 'android';

export const APP_DOWNLOAD_QR_SRC = '/assets/svgs/app-download-qr.svg';
export const APP_DOWNLOAD_AUTO_REDIRECT_SESSION_KEY =
  'app-download-auto-redirected';

interface AppDownloadDeviceInput {
  maxTouchPoints?: number;
  userAgent?: string;
  userAgentDataMobile?: boolean;
}

const getClientDeviceInput = (): Required<AppDownloadDeviceInput> => {
  if (typeof navigator === 'undefined') {
    return { maxTouchPoints: 0, userAgent: '', userAgentDataMobile: false };
  }

  return {
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    userAgent: navigator.userAgent,
    userAgentDataMobile:
      (
        navigator as Navigator & {
          userAgentData?: { mobile?: boolean };
        }
      ).userAgentData?.mobile ?? false,
  };
};

const getDeviceInput = (
  input: AppDownloadDeviceInput = {},
): Required<AppDownloadDeviceInput> => {
  const clientInput = getClientDeviceInput();

  return {
    maxTouchPoints: input.maxTouchPoints ?? clientInput.maxTouchPoints,
    userAgent: input.userAgent ?? clientInput.userAgent,
    userAgentDataMobile:
      input.userAgentDataMobile ?? clientInput.userAgentDataMobile,
  };
};

const isIPadOSDesktopUserAgent = (userAgent: string, maxTouchPoints: number) =>
  /iPad|Macintosh/i.test(userAgent) && maxTouchPoints > 1;

export const isMobileAppDownloadDevice = (
  input: AppDownloadDeviceInput = {},
) => {
  const { maxTouchPoints, userAgent, userAgentDataMobile } =
    getDeviceInput(input);

  if (userAgentDataMobile) return true;
  if (isIPadOSDesktopUserAgent(userAgent, maxTouchPoints)) return true;

  return /android|webos|iphone|ipod|blackberry|iemobile|opera mini|mobile|ipad/i.test(
    userAgent,
  );
};

export const getAppStoreTarget = (
  input: AppDownloadDeviceInput = {},
): AppStoreTarget | null => {
  const { maxTouchPoints, userAgent } = getDeviceInput(input);

  if (/iPhone|iPod/i.test(userAgent)) return 'ios';
  if (isIPadOSDesktopUserAgent(userAgent, maxTouchPoints)) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';

  return null;
};

export const getAppStoreUrls = (target: AppStoreTarget) => {
  if (target === 'ios') {
    return {
      fallbackUrl: APP_LINKS.ios.url,
      schemeUrl: `itms-apps://apps.apple.com/app/id${APP_LINKS.ios.app_store_id}`,
    };
  }

  return {
    fallbackUrl: APP_LINKS.android.url,
    schemeUrl: `market://details?id=${APP_LINKS.android.package}`,
  };
};
